import type { RefObject } from 'react'
import { useCallback, useEffect } from 'react'

import { advanceQueue } from '~/components/player/advance'
import { getNextIndex } from '~/components/player/next-index'
import type { AudioChannel, PlayerState, PlayOptions, QueueItem } from '~/components/player/types'
import { updateMediaSession } from '~/components/player/utils'
import { useAppStore } from '~/hooks/use-app-store'
import { streamUrl } from '~/lib/cover-art'
import { safePlay } from '~/lib/safe-play'

// A manual skip before this fraction of the track counts as a negative radio signal.
const RADIO_SKIP_FRACTION = 0.5

interface UsePlaybackControlsOptions {
  setState: (updater: PlayerState | ((prev: PlayerState) => PlayerState)) => void
  getActiveChannel: () => AudioChannel | null
  ensureAudioContext: () => AudioContext | null
  cancelCrossfade: () => void
  applyReplayGain: (channel: AudioChannel, track: QueueItem | null) => void
  notifyTimeSubscribers: () => void
  currentTrackIdRef: RefObject<number | null>
  scrobbledRef: RefObject<boolean>
  skipNextPlayRef: RefObject<boolean>
  pendingSeekRef: RefObject<number | null>
  pendingRadioAdvanceRef: RefObject<boolean>
  intentionalPauseRef: RefObject<boolean>
  currentTimeRef: RefObject<number>
  queue: QueueItem[]
  currentIndex: number
  radioMode: boolean
  shuffle: boolean
  repeat: PlayerState['repeat']
}

export function usePlaybackControls({
  setState,
  getActiveChannel,
  ensureAudioContext,
  cancelCrossfade,
  applyReplayGain,
  notifyTimeSubscribers,
  currentTrackIdRef,
  scrobbledRef,
  skipNextPlayRef,
  pendingSeekRef,
  pendingRadioAdvanceRef,
  intentionalPauseRef,
  currentTimeRef,
  queue,
  currentIndex,
  radioMode,
  shuffle,
  repeat,
}: UsePlaybackControlsOptions) {
  const play = useCallback(
    (tracks: QueueItem[], options: PlayOptions = {}) => {
      const { startIndex = 0, keepRadio = false } = options
      const radioOn = useAppStore.getState().playerSettings.radioMode

      ensureAudioContext()
      cancelCrossfade()

      // Load and play immediately within the user gesture so the browser allows audio.play()
      const track = tracks[startIndex]
      const channel = getActiveChannel()
      if (track && channel) {
        channel.audio.src = track.streamOverride ?? streamUrl(track.id)
        applyReplayGain(channel, track)
        safePlay(channel.audio, 'play')
        currentTrackIdRef.current = track.id
        scrobbledRef.current = false
        updateMediaSession(track)
      } else {
        currentTrackIdRef.current = null
      }

      // Radio mode has to clear in the same update as the queue swap. The Zustand
      // setting only reaches `state` through an effect, so writing it separately
      // leaves one render holding the new queue with radio still on — long enough
      // for the radio effect to seed off it and append to what the user just chose.
      if (!keepRadio && radioOn) useAppStore.getState().setPlayerSettings({ radioMode: false })
      setState((s) => ({
        ...s,
        queue: tracks,
        currentIndex: startIndex,
        radioMode: keepRadio && s.radioMode,
      }))
    },
    [
      cancelCrossfade,
      ensureAudioContext,
      getActiveChannel,
      applyReplayGain,
      currentTrackIdRef,
      scrobbledRef,
      setState,
    ]
  )

  const pause = useCallback(() => {
    intentionalPauseRef.current = true
    getActiveChannel()?.audio.pause()
  }, [getActiveChannel, intentionalPauseRef])

  const resume = useCallback(() => {
    ensureAudioContext()
    const audio = getActiveChannel()?.audio
    if (audio) safePlay(audio, 'resume')
  }, [getActiveChannel, ensureAudioContext])

  const togglePlay = useCallback(() => {
    const audio = getActiveChannel()?.audio
    if (!audio) return
    if (audio.paused) {
      ensureAudioContext()
      safePlay(audio, 'toggle-play')
    } else {
      intentionalPauseRef.current = true
      audio.pause()
    }
  }, [getActiveChannel, ensureAudioContext, intentionalPauseRef])

  // Simple one-step advance used by radio after fetching tracks — no radio truncation logic.
  const advanceOne = useCallback(() => {
    cancelCrossfade()
    currentTrackIdRef.current = null
    setState((prev) => {
      const nextIndex = prev.currentIndex + 1
      if (nextIndex >= prev.queue.length) return prev
      return { ...prev, currentIndex: nextIndex }
    })
  }, [cancelCrossfade, currentTrackIdRef, setState])

  const next = useCallback(() => {
    cancelCrossfade()
    // Manual skip early in a radio track is a negative taste signal.
    if (radioMode) {
      const cur = queue[currentIndex]
      if (cur && cur.duration > 0 && currentTimeRef.current < cur.duration * RADIO_SKIP_FRACTION) {
        useAppStore.getState().recordRadioDislike(cur.id)
      }
    }
    // Pre-check using current props so we only null the ref when we're actually
    // going to change tracks. If getNextIndex returns pending-radio, nulling here
    // would cause usePlaybackControls to reload the current track when the queue
    // truncation triggers a re-render.
    const precheck = getNextIndex({ queue, currentIndex, radioMode, shuffle, repeat })
    if (precheck.type !== 'pending-radio') {
      currentTrackIdRef.current = null
    }
    setState((prev) => {
      const outcome = advanceQueue(prev)
      // No radio tracks available yet — flag for auto-advance once they arrive.
      if (outcome.type === 'pending-radio') pendingRadioAdvanceRef.current = true
      return outcome.state
    })
  }, [
    cancelCrossfade,
    currentTrackIdRef,
    currentTimeRef,
    pendingRadioAdvanceRef,
    queue,
    currentIndex,
    radioMode,
    shuffle,
    repeat,
    setState,
  ])

  const prev = useCallback(() => {
    cancelCrossfade()
    setState((s) => {
      if (s.queue.length === 0) return s
      const audio = getActiveChannel()?.audio
      if (audio && audio.currentTime > 3) {
        audio.currentTime = 0
        return s
      }
      currentTrackIdRef.current = null
      const prevIndex = (s.currentIndex - 1 + s.queue.length) % s.queue.length
      return { ...s, currentIndex: prevIndex }
    })
  }, [cancelCrossfade, getActiveChannel, currentTrackIdRef, setState])

  const seek = useCallback(
    (time: number) => {
      const audio = getActiveChannel()?.audio
      if (audio) {
        audio.currentTime = time
        currentTimeRef.current = time
        notifyTimeSubscribers()
      }
    },
    [getActiveChannel, notifyTimeSubscribers, currentTimeRef]
  )

  // Load and play track when currentIndex changes
  useEffect(() => {
    const channel = getActiveChannel()
    if (!channel || currentIndex < 0 || currentIndex >= queue.length) return

    const track = queue[currentIndex]!

    // Skip reload if same track already loaded (e.g. queue reorder)
    if (currentTrackIdRef.current === track.id) return
    currentTrackIdRef.current = track.id

    const audio = channel.audio
    audio.src = track.streamOverride ?? streamUrl(track.id)
    // Must precede applyReplayGain — the gain node only exists once the context does,
    // and a no-op apply here would leave the track playing at unity.
    ensureAudioContext()
    applyReplayGain(channel, track)

    // If restoring from server, seek to the saved position instead of playing
    if (skipNextPlayRef.current) {
      skipNextPlayRef.current = false
      const pendingPos = pendingSeekRef.current
      if (pendingPos != null && pendingPos > 0) {
        pendingSeekRef.current = null
        audio.addEventListener('loadedmetadata', function onMeta() {
          audio.removeEventListener('loadedmetadata', onMeta)
          audio.currentTime = pendingPos
          currentTimeRef.current = pendingPos
          notifyTimeSubscribers()
        })
      }
    } else {
      safePlay(audio, 'track-change')
    }
    scrobbledRef.current = false
    updateMediaSession(track)
  }, [
    currentIndex,
    queue,
    getActiveChannel,
    applyReplayGain,
    ensureAudioContext,
    notifyTimeSubscribers,
    currentTimeRef,
    currentTrackIdRef,
    pendingSeekRef,
    scrobbledRef,
    skipNextPlayRef,
  ])

  // Register Media Session action handlers
  useEffect(() => {
    if (!('mediaSession' in navigator)) return
    navigator.mediaSession.setActionHandler('play', resume)
    navigator.mediaSession.setActionHandler('pause', pause)
    navigator.mediaSession.setActionHandler('previoustrack', prev)
    navigator.mediaSession.setActionHandler('nexttrack', next)
    return () => {
      navigator.mediaSession.setActionHandler('play', null)
      navigator.mediaSession.setActionHandler('pause', null)
      navigator.mediaSession.setActionHandler('previoustrack', null)
      navigator.mediaSession.setActionHandler('nexttrack', null)
    }
  }, [resume, pause, prev, next])

  return { play, pause, resume, togglePlay, next, prev, seek, advanceOne }
}
